import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { LocalStorageDefaults } from '@shared/defaults/local-storage-defaults';
import { UserData } from '@shared/models/users/user-data';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private userUrl = '/users/user';
  
  private _http = inject(HttpClient);
  private _authService = inject(AuthService);

  private _isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  private _userSubject$ = new BehaviorSubject<UserData | undefined>(undefined);

  public get isLoggedIn$(): Observable<boolean> {
    return combineLatest([
      this._isAuthenticatedSubject$,
      this._userSubject$
    ]).pipe(
      map(([isAuthenticated, user]) => {
        return isAuthenticated && !!user;
      }),
      distinctUntilChanged()
    );
  }
  public get user$() : Observable<UserData | undefined> {
    return this._userSubject$.asObservable();
  }

  constructor() {
    this._authService.isAuthenticated$.subscribe(authenticated => {
      this._isAuthenticatedSubject$.next(authenticated);

      if (authenticated && !this._userSubject$.value) {
        console.log('Retrieving User');

        this._http.get<UserData>(this.userUrl).subscribe({
          next: user => {
            this._userSubject$.next(user);
          },
          error: err => {
            console.error('Failed to retrieve user', err);

            if (err.status === 401 || err.status === 404) {
              this.logout();
            }
          }
        });
      }
    });
  }

  public login() {
    this._authService.loginWithRedirect({
      appState: { 
        targetRoute: window.location.pathname 
      },
    });
  }

  public signUp() {
    this._authService.loginWithRedirect({
      appState: { 
        targetRoute: window.location.pathname 
      },
      authorizationParams: {
        screen_hint: 'signup'
      }
    });
  }

  public logout() {
    this._userSubject$.next(undefined);
    this._isAuthenticatedSubject$.next(false);

    localStorage.setItem(
      LocalStorageDefaults.LogoutRedirectRoute, 
      window.location.pathname);

    this._authService.logout({ 
      logoutParams: { 
        returnTo: window.location.origin + '/logout' 
      } 
    });
  }
}
